import { type ChangeEventHandler, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Camera, FolderOpen, Images } from 'lucide-react';

import { closeModal } from '@/components/modals/modals.utils';
import { ModalBase } from '@/components/modals/modals-base';
import { Button } from '@/components/ui/button';
import { UploadImage } from '@/components/ui/upload-image';
import useClickOutside from '@/hooks/use-click-outside';
import { cn } from '@/lib/utils';

export interface MobileImageUploadModalProps {
  onUpload: (image: File) => void;
}

export const MobileImageUploadModal = ({ onUpload }: MobileImageUploadModalProps) => {
  const { t } = useTranslation();

  const modalContainerRef = useClickOutside<HTMLDivElement>(closeModal, true);
  const fileInputRef = useRef(null);

  const handleButtonClick = () => {
    if (fileInputRef.current !== null) {
      const fileInputRefCurrent: HTMLLabelElement = fileInputRef.current;
      fileInputRefCurrent.click();
    }
  };

  const handleUpload = (image: File) => {
    onUpload(image);
    closeModal();
  };

  const options = [
    {
      label: t('photoLibrary'),
      icon: Images,
      onClick: () => {},
    },
    {
      label: t('takePhoto'),
      icon: Camera,
      onClick: () => {},
    },
    {
      label: t('chooseFiles'),
      icon: FolderOpen,
      onClick: handleButtonClick,
    },
  ];

  return (
    <ModalBase
      ref={modalContainerRef}
      overlayWrapperClassname="flex sm:hidden"
      className="flex w-full max-w-[200px] flex-col overflow-hidden rounded-sm p-0 sm:hidden"
    >
      <UploadImage
        ref={fileInputRef}
        onUpload={handleUpload as unknown as ChangeEventHandler<HTMLInputElement>}
        hideIcon
      />
      {options.map((option, index) => (
        <Button
          key={`mobile-image-upload-option${String.fromCharCode(65 + index)}`}
          className={cn(
            'flex items-center justify-between px-2 py-1',
            index > 0 ? 'border-b-1 border' : null
          )}
          onClick={option.onClick}
        >
          <span className="text-sm">{option.label}</span>
          <option.icon className="size-5" />
        </Button>
      ))}
    </ModalBase>
  );
};
